import { memo, useCallback, useState, useEffect } from "react";
import Form from "../../components/Form";
import { getOwners, createAnimalsForSale } from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography } from "@mui/material";
import InformPopup from "../../components/popups/InformPopup.js";
import { BackButton } from "../../components/Buttons.js";

const CreateAnimalsToSale = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedOwner, setSelectedOwner] = useState();
    const [animalsAdded, setAnimalsAdded] = useState([]);
    const [animalsNotFound, setAnimalsNotFound] = useState([]);
    const [animalsAlreadyExist, setAnimalsAlreadyExist] = useState([]);
    const [lambsAdded, setLambsAdded] = useState([]);
    const [lambsNotFound, setLambsNotFound] = useState([]);
    const [lambsAlreadyExist, setLambsAlreadyExist] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const classes = useSelectStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                const dropdownValues = owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                }));
                setOwnersDropdownValues(dropdownValues);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const selectOwnerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο",
        },
    ]

    const handleOwnerSelect = (values) => {
        const selOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)
        setSelectedOwner(selOwner)
        setStep(2)
    }

    const setAnimalsFormContent = [
        {
            customType: "input",
            id: "animals",
            type: "text",
            placeholder: "12-ψήφιοι Κωδικοί Ζώων προς πώληση",
            helperText: "Μπορείτε να εισάγετε πολλούς κωδικούς χωρίζοντάς τους με κόμμα"
        },
        {
            customType: "input",
            id: "animalsPrice",
            type: "text",
            placeholder: "Τιμή Πώλησης Ζώων (€)",
        },
        {
            customType: "input",
            id: "lambs",
            type: "text",
            placeholder: "4-ψήφιοι Κωδικοί Αμνών πρς πώληση",
            helperText: "Μπορείτε να εισάγετε πολλούς κωδικούς χωρίζοντάς τους με κόμμα"
        },
        {
            customType: "input",
            id: "lambsPrice",
            type: "text",
            placeholder: "Τιμή Πώλησης Αμνών (€)",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Αποθήκευση",
        },
    ]

    const handleSubmit = async (values) => {
        setIsLoading(true);
        values.ownerId = selectedOwner._id;
        const { success: scs, data, message } = await createAnimalsForSale(values);
        if (scs) {
            setAnimalsAdded(data.animals.added);
            setAnimalsNotFound(data.animals.notFound);
            setAnimalsAlreadyExist(data.animals.alreadyExist);
            setLambsAdded(data.lambs.added);
            setLambsNotFound(data.lambs.notFound);
            setLambsAlreadyExist(data.lambs.alreadyExist);
            setIsPopupOpen(true);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            <InformPopup
                isOpen={isPopupOpen}
                title={""}
                onClose={() => {
                    setIsPopupOpen(false);
                    setStep(1);
                    setSelectedOwner(null);
                }}
                innerText={
                    [
                        animalsAdded.length > 0 && `- Τα ζώα ${animalsAdded.join(", ")} προστέθηκαν επιτυχώς`,
                        animalsNotFound.length > 0 && `- Τα ζώα ${animalsNotFound.join(", ")} δεν βρέθηκαν`,
                        animalsAlreadyExist.length > 0 && `- Τα ζώα ${animalsAlreadyExist.join(", ")} έχουν δηλωθεί ήδη προς πώληση`,
                        lambsAdded.length > 0 && `- Οι αμνοί ${lambsAdded.join(", ")} προστέθηκαν επιτυχώς`,
                        lambsNotFound.length > 0 && `- Οι αμνοί ${lambsNotFound.join(", ")} δεν βρέθηκαν`,
                        lambsAlreadyExist.length > 0 && `- Οι αμνοί ${lambsAlreadyExist.join(", ")} έχουν δηλωθεί ήδη προς πώληση`,
                    ]
                }

                buttonText={"Εντάξει"}
                onClick={() => {
                    setIsPopupOpen(false);
                    setStep(1);
                    setSelectedOwner(null);
                }}
            />
            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    {step === 1 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε Εκτροφή"}
                            </Typography>
                            <Form
                                content={selectOwnerFormContent}
                                onSubmit={handleOwnerSelect}
                            />
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Δήλωση Ζώων Προς Πώληση"}
                            </Typography>
                            <Form
                                content={setAnimalsFormContent}
                                onSubmit={handleSubmit}
                            />
                        </>
                    )}
                    {step !== 1 && (
                        <BackButton onClick={() => setStep(step - 1)} />
                    )}
                </Box>
            </Box>
        </>
    )
}

export default memo(CreateAnimalsToSale);
